import { z } from 'zod';

/**
 * RFC 4122 UUID. Convention: v4. Zod's `.uuid()` accepts any version; we don't
 * gate on version bits here because random v4 bytes are indistinguishable from
 * deliberate fixtures and reject of v1/v5 buys nothing for the runtime.
 */
export const Uuid = z.string().uuid();
export type Uuid = z.infer<typeof Uuid>;

/**
 * Path-style asset reference (max 256 chars). Resolved at build time against
 * the asset bundle. Restricted alphabet to keep configs printable and to
 * preempt path-traversal attempts (no `..` segments — the build pipeline
 * still owns asset resolution; this is belt-and-suspenders).
 */
export const AssetRef = z
  .string()
  .min(1)
  .max(256)
  .regex(/^[A-Za-z0-9._\-/]+$/, 'AssetRef must be alphanumerics with ._-/')
  .refine((s) => !s.split('/').includes('..'), 'AssetRef must not contain ".." segments');
export type AssetRef = z.infer<typeof AssetRef>;

/** ISO 8601 datetime with timezone offset (e.g., 2026-05-09T12:34:56Z). */
export const Iso8601 = z.string().datetime({ offset: true });
export type Iso8601 = z.infer<typeof Iso8601>;

/**
 * Parse a URL and return its lowercased scheme, or null when it isn't a
 * well-formed absolute URL. Shared by the URL primitives below.
 */
function urlScheme(raw: string): string | null {
  try {
    return new URL(raw).protocol.replace(/:$/, '').toLowerCase();
  } catch {
    return null;
  }
}

/**
 * An `http:`/`https:` URL. Plain `z.string().url()` also accepts `javascript:`,
 * `data:`, `intent:`, `file:` etc., which are dangerous when the runtime hands
 * the value to `window.location.href` or an Android intent — a hostile config
 * would get script execution or an arbitrary intent launch. Restrict the scheme
 * so those never validate. See also the defense-in-depth scheme check in the
 * renderer agent hosts' `openUrl`.
 */
export const HttpUrl = z
  .string()
  .url()
  .refine((u) => {
    const s = urlScheme(u);
    return s === 'http' || s === 'https';
  }, 'must be an http(s) URL');
export type HttpUrl = z.infer<typeof HttpUrl>;

/**
 * An `https:`-only URL. For endpoints the runtime POSTs to (telemetry, remote
 * config) — cleartext http would leak auth tokens / device data, so require TLS.
 */
export const HttpsUrl = z
  .string()
  .url()
  .refine((u) => urlScheme(u) === 'https', 'must be an https URL');
export type HttpsUrl = z.infer<typeof HttpsUrl>;

/** Lowercase 64-char hex SHA-256 digest. */
export const Sha256Hex = z
  .string()
  .length(64)
  .regex(/^[0-9a-f]{64}$/, 'must be 64 lowercase hex chars');
export type Sha256Hex = z.infer<typeof Sha256Hex>;

/**
 * Modular bcrypt hash (60 chars: `$2[aby]$<rounds>$<22-char-salt><31-char-hash>`).
 * The Builder hashes PINs in the browser before they touch storage; plaintext
 * PINs never enter config.
 */
export const BcryptHash = z
  .string()
  .regex(/^\$2[aby]\$\d{2}\$[./A-Za-z0-9]{53}$/, 'must be a bcrypt modular hash');
export type BcryptHash = z.infer<typeof BcryptHash>;

/** 24-hour HH:mm time string (00:00–23:59). */
export const TimeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be HH:mm 24-hour');
export type TimeOfDay = z.infer<typeof TimeOfDay>;

/** Day of week, 0=Sun..6=Sat (matches JS `Date.prototype.getDay`). */
export const DayOfWeek = z.number().int().min(0).max(6);
export type DayOfWeek = z.infer<typeof DayOfWeek>;

/** #RRGGBB or #RRGGBBAA hex color (case-insensitive). */
export const HexColor = z.string().regex(/^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/);
export type HexColor = z.infer<typeof HexColor>;

/**
 * Tenant identifier, 1–80 chars. UUID or org slug. Bound to `license.tenantId`;
 * mismatch causes the runtime to fall back to Core.
 */
export const TenantId = z.string().min(1).max(80);
export type TenantId = z.infer<typeof TenantId>;

/** Android package name (reverse-DNS, e.g., `com.example.app`). */
export const PackageName = z
  .string()
  .min(3)
  .max(255)
  .regex(/^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)+$/, 'must be a reverse-DNS package name');
export type PackageName = z.infer<typeof PackageName>;
